import { supabase } from '../lib/supabase';

export const reviewService = {
  /**
   * Get all reviews with pagination and search
   * @param {number} page - Page number (1-indexed)
   * @param {number} pageSize - Number of items per page
   * @param {string} searchQuery - Search query for filtering
   * @param {number} minRating - Minimum rating filter
   * @returns {Promise<{data: Array, count: number, error: any}>}
   */
  async getAll(page = 1, pageSize = 10, searchQuery = '', minRating = null) {
    try {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase?.from('csap_testimonials')?.select('*', { count: 'exact' });

      // Apply search filter
      if (searchQuery && searchQuery?.trim() !== '') {
        const search = searchQuery?.trim();
        query = query?.or(
          `client_first_name.ilike.%${search}%,client_last_initial.ilike.%${search}%,testimonial_text.ilike.%${search}%,program_impact.ilike.%${search}%`
        );
      }

      // Apply rating filter
      if (minRating && minRating >= 1 && minRating <= 5) {
        query = query?.gte('rating', minRating);
      }

      // Apply sorting
      query = query?.order('is_featured', { ascending: false })?.order('submitted_date', { ascending: false })?.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase
      const reviews = data?.map(row => ({
        id: row?.id,
        clientFirstName: row?.client_first_name,
        clientLastInitial: row?.client_last_initial,
        testimonialText: row?.testimonial_text,
        programImpact: row?.program_impact,
        rating: row?.rating,
        isFeatured: row?.is_featured,
        submittedDate: row?.submitted_date,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      })) || [];

      return { data: reviews, count, error: null };
    } catch (err) {
      console.error('Error fetching reviews:', err);
      return { data: [], count: 0, error: err };
    }
  },

  /**
   * Get featured reviews
   * @param {number} limit - Number of featured reviews to fetch
   * @returns {Promise<{data: Array, error: any}>}
   */
  async getFeatured(limit = 3) {
    try {
      const { data, error } = await supabase?.from('csap_testimonials')?.select('*')?.eq('is_featured', true)?.order('submitted_date', { ascending: false })?.limit(limit);

      if (error) throw error;

      // Convert snake_case to camelCase
      const reviews = data?.map(row => ({
        id: row?.id,
        clientFirstName: row?.client_first_name,
        clientLastInitial: row?.client_last_initial,
        testimonialText: row?.testimonial_text,
        programImpact: row?.program_impact,
        rating: row?.rating,
        isFeatured: row?.is_featured,
        submittedDate: row?.submitted_date,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      })) || [];

      return { data: reviews, error: null };
    } catch (err) {
      console.error('Error fetching featured reviews:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Get review count
   * @returns {Promise<{count: number, error: any}>}
   */
  async getCount() {
    try {
      const { count, error } = await supabase?.from('csap_testimonials')?.select('*', { count: 'exact', head: true });

      if (error) throw error;

      return { count: count || 0, error: null };
    } catch (err) {
      console.error('Error fetching review count:', err);
      return { count: 0, error: err };
    }
  }
};